import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { useFileViewer } from "@/components/app/sending/file-viewer";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useEveryApplication } from "./-components/use-every-application";
import { useCan } from "@/lib/policy";
import { readState } from "@/lib/query/read-state";
import { optionalOneOf, optionalString, searchPatch } from "@/lib/search";
import { EnvelopeOverview } from "./-components/envelope-overview";
import { CLOSED_STATUSES } from "@/routes/_authenticated/_workforce/hiring/-components/signature-group-shared";
import { SignatureGroup, type SigningDocument } from "./-components/signature-group";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/esign")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { filter?: "signed" | "all" | "everything"; status?: string } => ({
    filter: optionalOneOf(["signed", "all", "everything"], search.filter),
    status: optionalString(search.status) || undefined,
  }),
  component: EsignTracker,
});

const FILTERS = [
  { id: "outstanding", label: "Outstanding" },
  { id: "signed", label: "Signed" },
  { id: "all", label: "All" },
  /* The three above group by candidate at the signatures stage. This one is
     every envelope in the building, which is where a voided or an early one
     shows up. */
  { id: "everything", label: "Every document" },
] as const;

type Filter = (typeof FILTERS)[number]["id"];

const fail = (error: Error) => toast.error(errorWords(error));

const isOutstanding = (document: SigningDocument) =>
  !CLOSED_STATUSES.includes(document.envelopeStatus);

interface Voiding {
  applicationId: string;
  document: SigningDocument;
}

/** Every hiring signature still outstanding, in one place. The candidate's own page answers "where
 * is this person". This answers the other question: what is nobody chasing. */
function EsignTracker() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const { openFile, fileViewer } = useFileViewer();
  const canWrite = useCan("hiring:manage");
  const { filter: filterParam, status } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const filter: Filter = filterParam ?? "outstanding";
  const setFilter = (next: Filter) => patch({ filter: next === "outstanding" ? undefined : next });
  const [copiedEnvelopeId, setCopiedEnvelopeId] = useState<string | null>(null);
  const [issuingId, setIssuingId] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<Voiding | null>(null);
  const [reason, setReason] = useState("");

  const candidates = useEveryApplication({ facilityId, stage: "signatures" });
  const people = candidates.data?.rows ?? [];
  const envelopes = useQueries({
    queries: people.map((person) =>
      orpc.workforce.hiringSigning.list.queryOptions({ input: { applicationId: person.id } }),
    ),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.workforce.hiringSigning.key() });
  };

  const signLink = useMutation(
    orpc.workforce.hiringSigning.staffSignLink.mutationOptions({
      onSuccess: async (result, variables) => {
        await navigator.clipboard.writeText(new URL(result.url, window.location.origin).toString());
        setCopiedEnvelopeId(variables.envelopeId);
        toast.success("New link copied", {
          description: "A link copied before for this document no longer works.",
        });
        window.setTimeout(() => setCopiedEnvelopeId(null), 1500);
      },
      onError: fail,
    }),
  );
  const download = useMutation(
    orpc.administration.signing.signedPdf.mutationOptions({
      onSuccess: (result) => openFile(result.file),
      onError: fail,
    }),
  );
  const issue = useMutation(
    orpc.workforce.hiringSigning.issue.mutationOptions({
      onSuccess: async () => {
        toast.success("Documents sent");
        setIssuingId(null);
        await refresh();
      },
      onError: (error) => {
        setIssuingId(null);
        fail(error);
      },
    }),
  );
  const voidDocument = useMutation(
    orpc.workforce.hiringSigning.void.mutationOptions({
      onSuccess: async () => {
        toast.success("Document cancelled");
        setVoiding(null);
        setReason("");
        await refresh();
      },
      onError: fail,
    }),
  );

  const groups = people
    .map((person, index) => ({
      candidate: {
        id: person.id,
        applicantName: person.applicantName,
        jobTitle: person.jobTitle,
        daysInStage: person.daysInStage,
      },
      documents: envelopes[index]?.data ?? [],
    }))
    .toSorted((a, b) => b.candidate.daysInStage - a.candidate.daysInStage);

  const outstanding = groups.flatMap((group) => group.documents).filter(isOutstanding);
  const outstandingPeople = groups.filter((group) => group.documents.some(isOutstanding)).length;

  const shownFor = (documents: SigningDocument[]) =>
    filter === "signed"
      ? documents.filter((document) => document.envelopeStatus === "completed")
      : filter === "all"
        ? documents
        : documents.filter(isOutstanding);

  const visible = groups
    .map((group) => ({ ...group, shown: shownFor(group.documents) }))
    .filter((group) => filter === "all" || group.shown.length > 0);

  const loading = candidates.isPending || envelopes.some((query) => query.isPending);
  const failedReads = envelopes.filter((query) => readState(query) === "failed");
  const failed = candidates.isError || failedReads.length > 0;

  return (
    <>
      {fileViewer}
      <PageHeader
        title="Signatures"
        meta={
          loading
            ? "Loading…"
            : failed
              ? undefined
              : outstanding.length === 0
                ? "Nobody at the signatures stage is waiting to sign."
                : `${outstanding.length} document${outstanding.length === 1 ? "" : "s"} outstanding across ${outstandingPeople} candidate${outstandingPeople === 1 ? "" : "s"} at the signatures stage`
        }
      />

      <Tabs value={filter} onValueChange={(value) => setFilter(value as Filter)}>
        <TabsList>
          {FILTERS.map((entry) => (
            <TabsTrigger key={entry.id} value={entry.id}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {filter === "everything" ? (
        <EnvelopeOverview
          facilityId={facilityId}
          status={status}
          onStatus={(next) => patch({ status: next })}
        />
      ) : loading ? (
        <Skeleton className="h-64 w-full" />
      ) : failed ? (
        <ReadFailed
          message="Could not load the signature list. This is not a statement that nothing is outstanding."
          error={candidates.error ?? failedReads[0]?.error}
          onRetry={() => {
            if (candidates.isError) void candidates.refetch();
            for (const query of failedReads) void query.refetch();
          }}
          retrying={candidates.isFetching || failedReads.some((query) => query.isFetching)}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={
            filter === "outstanding"
              ? "Nothing outstanding"
              : filter === "signed"
                ? "Nothing signed yet"
                : "Nobody at the signatures stage"
          }
          description="Documents appear once a candidate reaches the signatures stage."
        />
      ) : (
        <div className="space-y-3">
          {visible.map((group) => (
            <SignatureGroup
              key={group.candidate.id}
              candidate={group.candidate}
              documents={group.shown}
              allDocuments={group.documents}
              canWrite={canWrite}
              copiedEnvelopeId={copiedEnvelopeId}
              issuing={issuingId === group.candidate.id}
              onIssue={() => {
                setIssuingId(group.candidate.id);
                issue.mutate({ applicationId: group.candidate.id });
              }}
              onCopyLink={(document) =>
                signLink.mutate({
                  applicationId: group.candidate.id,
                  envelopeId: document.envelopeId,
                })
              }
              onDownload={(document) => download.mutate({ envelopeId: document.envelopeId })}
              onVoid={(document) => setVoiding({ applicationId: group.candidate.id, document })}
            />
          ))}
        </div>
      )}

      <AlertDialog
        open={voiding !== null}
        onOpenChange={(open) => (open ? null : setVoiding(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {voiding?.document.formTemplateName}</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Label htmlFor="void-reason">Why</Label>
            <Textarea
              id="void-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={reason.trim().length === 0 || voidDocument.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (!voiding) return;
                voidDocument.mutate({
                  applicationId: voiding.applicationId,
                  envelopeId: voiding.document.envelopeId,
                  reason: reason.trim(),
                });
              }}
            >
              Cancel the document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
